import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mail, Phone, Trash2, Calendar, Users, FileText, Send } from "lucide-react";
import AdminQuotesList from "../components/AdminQuotesList";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";
import { apiClient } from "../lib/api";
import { toast, Toaster } from "sonner";

const TABS = [
  { key: "devis", label: "Demandes de devis", icon: Send },
  { key: "leads", label: "Simulations (ancien)", icon: Users },
  { key: "partners", label: "Partenaires", icon: FileText },
  { key: "contacts", label: "Contacts", icon: Mail },
];

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("devis");
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);

  const loadAll = async () => {
    try {
      const [s, q, l, p, c] = await Promise.all([
        apiClient.get("/admin/stats"),
        apiClient.get("/admin/devis"),
        apiClient.get("/admin/leads"),
        apiClient.get("/admin/partners"),
        apiClient.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setQuotes(q.data);
      setLeads(l.data);
      setPartners(p.data);
      setContacts(c.data);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };

  useEffect(() => {
    if (user) loadAll();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Supprimer ce lead ?")) return;
    await apiClient.delete(`/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    toast.success("Lead supprimé");
  };

  const deletePartner = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    await apiClient.delete(`/admin/partners/${id}`);
    setPartners((ps) => ps.filter((p) => p.id !== id));
    toast.success("Candidature supprimée");
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" richColors />
      <header className="border-b border-border bg-white">
        <div className="container-tm py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size="default" />
            <span className="pill">Admin</span>
          </div>
          <button onClick={handleLogout} className="btn-ghost text-sm py-2.5 px-5" data-testid="admin-logout">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="container-tm py-10">
        {/* Stats */}
        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10" data-testid="admin-stats">
            <Stat label="Demandes de devis" value={stats.devis_total} />
            <Stat label="Devis à traiter" value={stats.devis_new} accent />
            <Stat label="Candidatures" value={stats.partners_total} />
            <Stat label="En attente" value={stats.partners_pending} accent />
            <Stat label="Contacts" value={stats.contacts_total} />
          </div>
        )}

        {/* Tabs */}
        <div className="inline-flex flex-wrap gap-1 bg-white border border-border rounded-full p-1.5 mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-5 py-2 text-sm font-medium rounded-full transition-colors flex items-center gap-2 ${
                tab === t.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "devis" && (
          <AdminQuotesList quotes={quotes} setQuotes={setQuotes} partners={partners} onChange={() => apiClient.get("/admin/stats").then((r) => setStats(r.data))} />
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="leads-list">
            {leads.length === 0 ? (
              <Empty label="Aucun lead pour le moment" />
            ) : (
              leads.map((l) => (
                <div key={l.id} className="surface-card p-5 grid md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-3">
                    <div className="font-semibold">{l.first_name} {l.last_name}</div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Calendar size={11} /> {new Date(l.created_at).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <div className="md:col-span-4 text-sm space-y-1">
                    <div className="flex items-center gap-1.5"><Mail size={12} className="text-accent" /> {l.email}</div>
                    <div className="flex items-center gap-1.5"><Phone size={12} className="text-accent" /> {l.phone}</div>
                    {l.city_slug && <div className="text-muted-foreground text-xs">Ville : {l.city_slug}</div>}
                  </div>
                  <div className="md:col-span-4 text-sm">
                    {l.simulator_low && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Estimation simulateur</div>
                        <div className="font-bold text-xl text-accent tracking-tight" style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
                          {l.simulator_low.toLocaleString("fr-FR")} – {l.simulator_high.toLocaleString("fr-FR")} €
                        </div>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {l.bedrooms} ch · {l.capacity} pers
                      {l.sea_view && " · vue mer"}
                      {l.parking && " · parking"}
                    </div>
                  </div>
                  <div className="md:col-span-1 text-right">
                    <button onClick={() => deleteLead(l.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-lead-${l.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "partners" && (
          <div className="space-y-3" data-testid="partners-list">
            {partners.length === 0 ? (
              <Empty label="Aucune candidature pour le moment" />
            ) : (
              partners.map((p) => (
                <div key={p.id} className="surface-card p-5 grid md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-3">
                    <div className="font-semibold">{p.first_name} {p.last_name}</div>
                    {p.company_name && <div className="text-xs text-muted-foreground">{p.company_name}</div>}
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <Calendar size={11} /> {new Date(p.created_at).toLocaleString("fr-FR")}
                    </div>
                  </div>
                  <div className="md:col-span-3 text-sm space-y-1">
                    <div className="flex items-center gap-1.5"><Mail size={12} className="text-accent" /> {p.email}</div>
                    <div className="flex items-center gap-1.5"><Phone size={12} className="text-accent" /> {p.phone}</div>
                    <div className="text-muted-foreground text-xs">Ville : {p.city}</div>
                    {p.zone && <div className="text-xs font-semibold text-accent">Secteur : {p.zone}</div>}
                    {p.plan && <div className="text-xs text-muted-foreground">Offre : {p.plan}</div>}
                  </div>
                  <div className="md:col-span-2 text-sm">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Expérience</div>
                    <div className="font-medium">{p.years_experience} ans · {p.properties_managed} biens</div>
                  </div>
                  <div className="md:col-span-3 text-xs text-muted-foreground line-clamp-3">
                    {p.motivation}
                  </div>
                  <div className="md:col-span-1 text-right">
                    <button onClick={() => deletePartner(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-partner-${p.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-3" data-testid="contacts-list">
            {contacts.length === 0 ? (
              <Empty label="Aucun message" />
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="surface-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="font-semibold">{c.name} : {c.subject}</div>
                    <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-2 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Mail size={11} className="text-accent" /> {c.email}</span>
                    {c.phone && <span className="flex items-center gap-1"><Phone size={11} className="text-accent" /> {c.phone}</span>}
                  </div>
                  <p className="text-sm mt-3 text-foreground/85">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`surface-card p-5 ${accent ? "border-accent bg-accent/5" : ""}`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`mt-2 font-bold text-3xl tracking-tight ${accent ? "text-accent" : ""}`} style={{ fontFamily: "Quicksand, sans-serif", letterSpacing: "-0.01em" }}>
        {value}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="text-center py-16 text-muted-foreground text-sm">{label}</div>;
}
